// Inline AI assist for smart duplicate: user describes BOM deltas and the
// existing refine-draft endpoint applies them to the copied material lines.

import { useCallback, useState } from "react";
import type { DuplicateRecipeDraft } from "@/lib/manufacturing/duplicateRecipeDraft";
import {
  duplicateFormPatchFromDictated,
  duplicateFormToDictatedDraft,
  duplicateRefineTranscript,
} from "@/lib/manufacturing/duplicateRecipeDraftBridge";
import { refineDictatedRecipeDraft } from "@/lib/manufacturing/recipesDictationClient";
import { summarizeDictatedRecipeDraftDiff } from "@/lib/manufacturing/dictatedRecipeDraftDiff";
import type { MfgRecipeKind } from "@/lib/manufacturing/types";
import { inputClass, mfgSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export function ManufacturingModelsSmartDuplicateAssist({
  duplicateDraft,
  code,
  name,
  kind,
  tagsInput,
  busy,
  onApplied,
  onError,
}: {
  duplicateDraft: DuplicateRecipeDraft;
  code: string;
  name: string;
  kind: MfgRecipeKind;
  tagsInput: string;
  busy: boolean;
  onApplied: (patch: ReturnType<typeof duplicateFormPatchFromDictated>) => void;
  onError: (message: string) => void;
}) {
  const [description, setDescription] = useState("");
  const [applying, setApplying] = useState(false);
  const [lastDiff, setLastDiff] = useState<string | null>(null);

  const handleApply = useCallback(async () => {
    const turn = description.trim();
    if (!turn || applying) return;
    setApplying(true);
    setLastDiff(null);
    onError("");

    const priorDraft = duplicateFormToDictatedDraft(
      duplicateDraft,
      code,
      name,
      kind,
      tagsInput,
    );
    const result = await refineDictatedRecipeDraft({
      transcript: duplicateRefineTranscript(duplicateDraft),
      priorDraft,
      turn,
    });

    setApplying(false);
    if (!result.ok || !result.data.ok || !result.data.draft) {
      const errMsg = !result.ok
        ? result.error || "AI refinement failed."
        : "AI could not apply those changes.";
      onError(errMsg);
      return;
    }

    const patch = duplicateFormPatchFromDictated(duplicateDraft, result.data.draft);
    setLastDiff(summarizeDictatedRecipeDraftDiff(priorDraft, result.data.draft));
    onApplied(patch);
  }, [
    applying,
    code,
    description,
    duplicateDraft,
    kind,
    name,
    onApplied,
    onError,
    tagsInput,
  ]);

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/50 px-3 py-3 dark:border-violet-800 dark:bg-violet-950/25">
      <div className="text-xs font-semibold text-violet-950 dark:text-violet-100">
        Describe what&apos;s different
      </div>
      <p className="mt-1 text-[11px] text-violet-900/90 dark:text-violet-100/85">
        Example: same floor, but 2×4×117 studs become 2×6×117 and 2×4×192 studs become
        2×6×192. AI will adjust materials, code, and name — then review before saving.
      </p>
      <textarea
        className={`${inputClass} mt-2 min-h-[72px] resize-y text-sm`}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What's different from the source model?"
        disabled={busy || applying}
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={mfgSecondaryBtnClass}
          onClick={() => void handleApply()}
          disabled={busy || applying || !description.trim()}
        >
          {applying ? "Applying…" : "Apply with AI"}
        </button>
        {lastDiff ? (
          <span className="text-[11px] text-violet-900/90 dark:text-violet-100/85">
            {lastDiff}
          </span>
        ) : null}
      </div>
    </div>
  );
}
